/**
 * TEMPORARY local contracts mirror for the coarse People & Access (users) DTOs +
 * the permission model.
 *
 * Plan 22-01 added the `subsystem-users-v2` flag key to `@hbs-us/contracts` SOURCE
 * (1.7.0), but the dashboard's INSTALLED `@hbs-us/contracts` lags, and the People &
 * Access list/permission DTOs are not published yet. Republishing the package is
 * owner-gated CI (gitleaks/license/cosign, GitHub Packages) and cannot run here.
 * Following the established Phase 19 + 21 + 22-05 pattern (the sibling
 * `employees/contractsMirror.ts` + `invoices/contractsMirror.ts`), these types are
 * mirrored locally, shaped IDENTICALLY to the 22-06 carve handler responses
 * (`qelm-backend/workers/api/src/routes/api.ts` `GET /api/v1/users` +
 * `PATCH /api/v1/users/:id/permissions`) AND the permission model in
 * `qelm-backend/.../services/permissions.ts`, and marked with the carry below.
 *
 * The PermissionKey / PermissionOverrideValue shapes are preserved EXACTLY from the
 * carve permissions service so the matrix renders the role-preset + per-override model
 * faithfully (SEC-sensitive — a drift here is a privilege-rendering bug).
 *
 * TODO(contracts republish): delete this file and import these from `@hbs-us/contracts`
 * once the People & Access DTOs are published and pinned in this repo's package.json.
 */

/** The three tenant roles, in precedence order (Admin first). */
export type UserRole = 'Admin' | 'Manager' | 'Employee';

/**
 * The canonical permission keys (preserved BYTE-FOR-BYTE from the carve permissions
 * service `PERMISSIONS`). A drift here mis-renders the matrix, so the list is mirrored
 * exactly; the server is authoritative on resolution.
 */
export const PERMISSION_KEYS = [
  'jobs.view',
  'jobs.create',
  'jobs.edit',
  'jobs.archive',
  'employees.view',
  'employees.create',
  'employees.edit',
  'employees.archive',
  'time.view',
  'time.clock',
  'time.edit_requests',
  'time.approve',
  'invoices.view',
  'invoices.create',
  'invoices.edit',
  'invoices.archive',
  'payments.manage',
  'fleet.view',
  'fleet.manage',
  'financials.view',
  'financials.edit',
  'reports.view',
  'activity.view',
  'settings.view',
  'settings.manage',
  'users.view',
  'users.create',
  'users.edit',
  'users.deactivate',
  'billing.view',
  'billing.manage',
  'inventory.view',
  'inventory.manage',
  'inventory.categories',
  'inventory.archive',
  // Quality control. Distinct from the `quality_control` TIER feature, which decides whether the
  // tenant is paying for QC at all — these decide who inside a paying tenant may do what.
  'qc.view',
  'qc.complete',
  'qc.approve',
  'qc.manage_templates',
] as const;

export type PermissionKey = (typeof PERMISSION_KEYS)[number];

/** The per-user override state for a single permission (the matrix toggle's 3-state). */
export type PermissionOverrideValue = 'default' | 'allow' | 'deny';

/** A permission grouped under a labelled section (mirrors `getPermissionGroups`). */
export interface PermissionGroup {
  label: string;
  permissions: PermissionKey[];
}

/**
 * The labelled permission groups the matrix renders as column sections. Mirrors the
 * carve `getPermissionGroups()` grouping (the labels are translated client-side via the
 * `users:permissions.keys.*` map, so only the grouping/keys are carried here).
 */
export const PERMISSION_GROUPS: readonly PermissionGroup[] = [
  { label: 'jobs', permissions: ['jobs.view', 'jobs.create', 'jobs.edit', 'jobs.archive'] },
  {
    label: 'employees',
    permissions: ['employees.view', 'employees.create', 'employees.edit', 'employees.archive'],
  },
  { label: 'time', permissions: ['time.view', 'time.clock', 'time.edit_requests', 'time.approve'] },
  {
    label: 'invoices',
    permissions: [
      'invoices.view',
      'invoices.create',
      'invoices.edit',
      'invoices.archive',
      'payments.manage',
    ],
  },
  {
    label: 'financials',
    permissions: ['financials.view', 'financials.edit', 'reports.view', 'activity.view'],
  },
  {
    label: 'qc',
    permissions: ['qc.view', 'qc.complete', 'qc.approve', 'qc.manage_templates'],
  },
  {
    label: 'inventory',
    permissions: [
      'inventory.view',
      'inventory.manage',
      'inventory.categories',
      'inventory.archive',
    ],
  },
  { label: 'fleet', permissions: ['fleet.view', 'fleet.manage'] },
  {
    label: 'access',
    permissions: [
      'users.view',
      'users.create',
      'users.edit',
      'users.deactivate',
      'billing.view',
      'billing.manage',
      'settings.view',
      'settings.manage',
    ],
  },
] as const;

export interface UserPermissionStateDto {
  id: number;
  role: UserRole;
  overrides: Partial<Record<PermissionKey, PermissionOverrideValue>>;
  rolePermissions: PermissionKey[];
  effectivePermissions: PermissionKey[];
}

/** The coarse list-item the People & Access screen renders (CARVE-07). */
export interface UserListItemDto extends UserPermissionStateDto {
  name: string;
  email: string;
  /** 1 = active, 0 = inactive. */
  active: number;
  /** Linked employee record id (nullable). */
  employeeId: number | null;
  /** Linked employee display name (nullable — joined, tenant-scoped). */
  employeeName: string | null;
}

/** Shared API envelope shape (matches `@hbs-us/contracts` `ApiEnvelope`). */
interface ApiEnvelope {
  ok: boolean;
  error?: string;
}

/**
 * Coarse `GET /api/v1/users` response (22-06): rows + the server-computed role + active
 * counts. Every payload field is optional so the failure case stays well-typed.
 */
export interface UsersResponse extends ApiEnvelope {
  users?: UserListItemDto[];
  rolePresets?: Record<UserRole, PermissionKey[]>;
  /** Count per role (filter-independent — drives the role-filter pill counts). */
  roleCounts?: Partial<Record<UserRole, number>>;
  /** active/inactive counts (the status summary). */
  activeCounts?: { active: number; inactive: number };
  /** Total users in the workspace (the "All" pill count). */
  totalUsers?: number;
}

/**
 * Response of the permission-override `PATCH /api/v1/users/:id/permissions` (22-06): the
 * server returns the RESOLVED override map + the effective permission set after applying
 * the change (the matrix re-syncs to the authoritative server state on success).
 */
export interface UserPermissionsPatchInput {
  role?: UserRole;
  overrides?: Partial<Record<PermissionKey, PermissionOverrideValue>>;
}

export interface UserPermissionsPatchResponse extends ApiEnvelope {
  user?: UserPermissionStateDto;
}

export function mergePermissionPatchResponse(
  current: UsersResponse | undefined,
  userId: number,
  response: UserPermissionsPatchResponse,
): UsersResponse | undefined {
  if (!current?.users || !response.user) return current;
  return {
    ...current,
    users: current.users.map((user) => (user.id === userId ? { ...user, ...response.user } : user)),
  };
}

/**
 * The invite (create-user) payload (POST /api/v1/users, Plan 22-24). Adds the optional
 * `employeeId` link; the server enforces the Employee-role-must-link rule + validates the
 * employee is active in the tenant. NO tenantId (SEC-11).
 */
export interface UserInviteInput {
  name: string;
  email: string;
  role: UserRole;
  password: string;
  /** Optional linked employee id (required when role === 'Employee'). */
  employeeId?: number | null;
}

/**
 * The edit-user-record payload (PATCH /api/v1/users/:id, Plan 22-24). Every field is
 * optional/additive: name / email (tenant-unique) / employeeId re-link / active toggle /
 * newPassword (admin reset). The server enforces the self-deactivation + last-active-Admin
 * guards and returns a stable error code on rejection.
 */
export interface UserPatchInput {
  name?: string;
  email?: string;
  employeeId?: number | null;
  active?: boolean;
  newPassword?: string;
}

/** Response of the edit-user PATCH (22-24). `error` carries the guard code on 4xx. */
export interface UserPatchResponse extends ApiEnvelope {
  user?: {
    id: number;
    name: string;
    email: string;
    active: number;
    employeeId: number | null;
    passwordChanged: boolean;
  };
}
